import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { FilePlus2Icon, ScaleIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { TERMS_KEY, termsApi } from '@/api/terms'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { TermsDraft } from '@/components/terms/terms-draft'
import { TermsHistory } from '@/components/terms/terms-history'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/format'
import { versionLine } from '@/lib/terms'

export const Route = createFileRoute('/_app/settings/terms')({
  component: SettingsTermsPage,
})

const DRAFT_REASONS: Record<string, string> = {
  terms_draft_exists: 'Un brouillon existe déjà : il est ci-dessous.',
  terms_not_published: 'Aucune version n’est publiée : il n’y a rien à copier.',
}

/**
 * Paramètres › Conditions générales (lot 42, contrat 1.85.0) : la version
 * globale en vigueur et son brouillon, l'historique, et les clients qui ont
 * leur propre version, avec leur écart. Rédiger est permis à un membre ;
 * publier, à un administrateur.
 */
function SettingsTermsPage() {
  const queryClient = useQueryClient()
  const overview = useQuery({ queryKey: [...TERMS_KEY, 'overview'], queryFn: termsApi.overview })
  const create = useMutation({
    mutationFn: () => termsApi.createDraft({}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TERMS_KEY })
    },
  })

  if (overview.isPending) {
    return (
      <Frame>
        <Skeleton className="h-40 w-full" />
      </Frame>
    )
  }
  if (overview.isError) {
    return (
      <Frame>
        <EmptyState
          icon={ScaleIcon}
          title="Les conditions générales n’ont pas pu être lues"
          description="Réessayez dans un instant."
        />
      </Frame>
    )
  }
  const { global_current: current, global_draft: draft, forks } = overview.data

  return (
    <div className="space-y-6" data-testid="parametres-conditions">
      <Frame>
        <section className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="font-semibold tracking-tight">Conditions générales des soumissions</h2>
              <p className="text-sm text-muted-foreground">
                Jointes à chaque soumission envoyée : les clauses communes, puis une annexe par type
                de vente. Une soumission envoyée garde la version qu’elle porte.
              </p>
              <p className="text-sm" data-testid="version-globale">
                <span className="font-medium">En vigueur : </span>
                {current
                  ? versionLine(current)
                  : 'aucune version publiée — aucune condition n’est jointe aux soumissions.'}
              </p>
            </div>
            {!draft && (
              <Button
                type="button"
                variant="outline"
                disabled={create.isPending}
                onClick={() => {
                  create.mutate()
                }}
              >
                <FilePlus2Icon />
                {current ? 'Préparer une nouvelle version' : 'Rédiger la première version'}
              </Button>
            )}
          </div>
          {create.error && (
            <FormError>
              {create.error instanceof ApiError && create.error.reason
                ? (DRAFT_REASONS[create.error.reason] ?? create.error.message)
                : 'Le brouillon n’a pas pu être créé.'}
            </FormError>
          )}
          {draft && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">
                Brouillon de la version {draft.version}
                <span className="ml-2 font-normal text-muted-foreground">
                  modifié le {formatDateTime(draft.updated_at)}
                </span>
              </h3>
              <TermsDraft versionId={draft.id} />
            </div>
          )}
        </section>
      </Frame>

      <Frame>
        <section className="space-y-3" aria-label="Versions propres à des clients">
          <h2 className="font-semibold tracking-tight">Versions propres à des clients</h2>
          {forks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun client n’a sa propre version : tous reçoivent les conditions globales. Une
              version propre se crée depuis la page « Conditions » d’un client.
            </p>
          ) : (
            <ul className="divide-y rounded-lg border" data-testid="forks-conditions">
              {forks.map((fork) => (
                <li
                  key={fork.client.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm"
                  data-testid="fork-conditions"
                >
                  <Link
                    to="/clients/$clientId/terms"
                    params={{ clientId: fork.client.id }}
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {fork.client.name}
                  </Link>
                  <span className="text-muted-foreground">
                    {fork.current
                      ? `version ${String(fork.current.version)}, copiée de la globale ${String(fork.current.based_on_global_version ?? '—')}`
                      : 'pas encore publiée'}
                  </span>
                  {fork.draft && <Badge variant="secondary">Brouillon en cours</Badge>}
                  {fork.behind_global && (
                    <Badge variant="outline" className="border-warning text-warning-strong">
                      En retard sur la globale {current?.version ?? ''}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </Frame>

      <Frame>
        <TermsHistory scope="global" />
      </Frame>
    </div>
  )
}
